import 'regenerator-runtime/runtime';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { GlobalActions, GlobalPut } from '@ps20-frontend/shop-integration-redux';
import { BankserviceRequest, BankserviceResponse, BankValidierungsStatus } from '@ps20/bff-models';
import { App, formatBankname, formatIban } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { CheckoutAction, CheckoutActions } from '../checkout.actions';
import { BankdatenPayload } from '../action-types/bankdaten';
import { IAppState } from '../localStore';

export const URL_VALIDIEREIBAN = '/privatschutz/api/bankService';

export enum VALIDIEREIBAN_REQUEST_EVENTS {
  VALIDIEREIBAN_REQUEST_SUCCESSFUL = 'VALIDIEREIBAN_REQUEST_SUCCESSFUL',
  VALIDIEREIBAN_REQUEST_FAILED = 'VALIDIEREIBAN_REQUEST_FAILED',
}

export function* watchBankdatenReceived() {
  yield takeLatest(
    GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL,
    safeSaga(validiereIbanAusKdvSaga, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    CheckoutActions.ZAHLUNG_IBAN_CHANGED,
    safeSaga(validiereIbanSaga, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    CheckoutActions.ZAHLUNG_IBAN_VALIDATED,
    safeSaga(validiereIbanSaga, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    VALIDIEREIBAN_REQUEST_EVENTS.VALIDIEREIBAN_REQUEST_SUCCESSFUL,
    safeSaga(successCallback, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    VALIDIEREIBAN_REQUEST_EVENTS.VALIDIEREIBAN_REQUEST_FAILED,
    safeSaga(errorCallback, App.SHOP_CHECKOUT),
  );
}

export function* validiereIbanAusKdvSaga() {
  const state: IAppState = yield select();
  const bankDaten = {
    iban: state.zahlung.iban,
    bic: state.zahlung.bic,
  };
  if (bankDaten.iban) {
    const bankdatenRequest: BankserviceRequest = {
      iban: bankDaten.iban.replaceAll(/ /g, ''),
    };
    if (bankDaten.bic) {
      bankdatenRequest.bic = bankDaten.bic;
    }
    yield callBankdatenservice(bankdatenRequest);
  }
}

export function* validiereIbanSaga(action: CheckoutAction<BankserviceResponse>) {
  const bankdatenRequest: BankserviceRequest = {
    iban: action.payload.iban.replaceAll(/ /g, ''),
    bic: action.payload.swiftcode,
  };
  yield callBankdatenservice(bankdatenRequest);
}

function isDeutscheIban(iban: string): boolean | undefined {
  if (iban && iban.length > 0) {
    return iban.startsWith('DE');
  }
}

export function* callBankdatenservice(bankdatenRequest: BankserviceRequest) {
  if (
    isDeutscheIban(bankdatenRequest.iban) ||
    (!isDeutscheIban(bankdatenRequest.iban) &&
      bankdatenRequest.bic &&
      bankdatenRequest.bic.length > 0)
  ) {
    yield call(callBackend, bankdatenRequest);
  }
}

export function* callBackend(bankdatenRequest: BankserviceRequest) {
  const globalPut = GlobalPut.getInstance();

  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      appId: App.SHOP_CHECKOUT,
      body: bankdatenRequest,
      url: URL_VALIDIEREIBAN,
      successEvent: VALIDIEREIBAN_REQUEST_EVENTS.VALIDIEREIBAN_REQUEST_SUCCESSFUL,
      errorEvent: VALIDIEREIBAN_REQUEST_EVENTS.VALIDIEREIBAN_REQUEST_FAILED,
    }),
  );
}

export function* successCallback(successAction: CheckoutAction<BankserviceResponse>) {
  const resp: BankserviceResponse = successAction.payload;
  const state = resp.bankValidierungsStatus;

  if (state === BankValidierungsStatus.OK) {
    yield put(new CheckoutActions().bankdatenReceived(convertToBankserviceResponse(resp)));
  } else {
    yield put(new CheckoutActions().bankdatenFailed());
  }
}

function convertToBankserviceResponse(resp: BankserviceResponse): BankdatenPayload {
  const bankname = formatBankname(resp);
  return {
    iban: formatIban(resp.iban),
    bankname,
    bic: resp.swiftcode,
  };
}

export function* errorCallback() {
  yield put(new CheckoutActions().bankdatenNotReceived());
}
